import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../../../../models/UiUtils';
import { FileRequestLinkPlaceholder } from '../../../../../../models/Token';
import { contentHasFileRequestToken } from '../../../../../../models/utils/richContentTokenUtils';
import { Button } from '../../../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../../components-v2/Dialog';
import { Field, FieldError, FieldLabel } from '../../../../../components-v2/Field';
import { Input } from '../../../../../components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components-v2/Select';
import { TextArea } from '../../../../../components-v2/TextArea';
import { TextCampaignEditor } from '../../../../TextCampaignEditor';
import { styleSheet } from './styles';

interface ReminderModalProps {
	isOpen: boolean;
	reminderOptions: Api.IMessageReminderOptions;
	hasSubject: boolean;
	onSave: (opts: Api.IMessageReminderOptions) => void;
	onRequestClose: () => void;
}

const INTERVAL_OPTIONS = [1, 2, 3, 5, 7, 10, 14, 21, 30];
const MAX_ATTEMPTS_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function withCurrentValue(options: number[], current: number): number[] {
	return options.includes(current) ? options : [...options, current].sort((a, b) => a - b);
}

export function ReminderModal({ isOpen, reminderOptions, hasSubject, onSave, onRequestClose }: ReminderModalProps) {
	const [subject, setSubject] = React.useState(reminderOptions.subject ?? '');
	const [intervalInDays, setIntervalInDays] = React.useState(reminderOptions.intervalInDays);
	const [maxAttempts, setMaxAttempts] = React.useState(reminderOptions.maxAttempts);
	const [emailEditorState, setEmailEditorState] = React.useState<Api.IRichContentEditorState>(
		convertRawRichTextContentStateToRichContentEditorState(reminderOptions.content)
	);
	const [textContent, setTextContent] = React.useState(
		Api.getContentHtmlStringValueFromRawRichTextContentState(reminderOptions.content) ?? ''
	);
	const [subjectError, setSubjectError] = React.useState<string>();
	const [contentError, setContentError] = React.useState<string>();

	React.useEffect(() => {
		if (isOpen) {
			setSubject(reminderOptions.subject ?? '');
			setIntervalInDays(reminderOptions.intervalInDays);
			setMaxAttempts(reminderOptions.maxAttempts);
			setEmailEditorState(convertRawRichTextContentStateToRichContentEditorState(reminderOptions.content));
			setTextContent(Api.getContentHtmlStringValueFromRawRichTextContentState(reminderOptions.content) ?? '');
			setSubjectError(undefined);
			setContentError(undefined);
		}
	}, [isOpen]);

	const handleSave = () => {
		let hasError = false;

		if (hasSubject && !subject.trim()) {
			setSubjectError('Please enter a subject.');
			hasError = true;
		}

		const content = hasSubject
			? emailEditorState.getRawRichTextContent()
			: Api.createRawRichTextContentStateWithHtmlStringValue(textContent);

		if (!contentHasFileRequestToken(content)) {
			setContentError(`The reminder message must include the ${FileRequestLinkPlaceholder.symbol} merge field.`);
			hasError = true;
		}

		if (hasError) return;

		onSave({
			...reminderOptions,
			subject: hasSubject ? subject : undefined,
			content,
			intervalInDays,
			maxAttempts,
		});
	};

	return (
		<Dialog
			open={isOpen}
			onOpenChange={open => {
				if (!open) onRequestClose();
			}}
		>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Configure File Request Reminder</DialogTitle>
				</DialogHeader>
				<DialogBody styles={[styleSheet.modalBody]}>
					<Field>
						<FieldLabel>Send reminder every</FieldLabel>
						<Select value={String(intervalInDays)} onValueChange={v => setIntervalInDays(Number(v))}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{withCurrentValue(INTERVAL_OPTIONS, intervalInDays).map(n => (
									<SelectItem key={n} value={String(n)}>
										{n} {n === 1 ? 'day' : 'days'}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</Field>
					<Field>
						<FieldLabel>Max reminders</FieldLabel>
						<Select value={String(maxAttempts)} onValueChange={v => setMaxAttempts(Number(v))}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{withCurrentValue(MAX_ATTEMPTS_OPTIONS, maxAttempts).map(n => (
									<SelectItem key={n} value={String(n)}>
										{n}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</Field>
					{hasSubject && (
						<Field>
							<FieldLabel>Reminder subject</FieldLabel>
							<Input
								value={subject}
								onChange={e => {
									setSubject(e.target.value);
									setSubjectError(undefined);
								}}
								placeholder='Enter reminder subject'
							/>
							{subjectError && <FieldError>{subjectError}</FieldError>}
						</Field>
					)}
					<Field>
						<FieldLabel>Reminder message</FieldLabel>
						{hasSubject ? (
							<div className={css(styleSheet.editorWrapper)}>
								<TextCampaignEditor
									contentState={emailEditorState}
									onContentStateChanged={state => {
										setEmailEditorState(state);
										setContentError(undefined);
									}}
								/>
							</div>
						) : (
							<TextArea
								value={textContent}
								onChange={e => {
									setTextContent(e.target.value);
									setContentError(undefined);
								}}
								placeholder={`Include ${FileRequestLinkPlaceholder.symbol} in your message`}
								rows={4}
							/>
						)}
						{contentError && <FieldError>{contentError}</FieldError>}
					</Field>
				</DialogBody>
				<DialogFooter>
					<Button variant='secondary' onClick={onRequestClose}>
						Cancel
					</Button>
					<Button variant='primary' onClick={handleSave}>
						Save
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}
